import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, filter, take, tap } from 'rxjs';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { WorkspacesSearchCriterionsFactory } from 'src/app/core/factories/workspaces-search-criterions.factory';
import { Criterion, SearchItemModel } from 'src/app/core/services/base-event-service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { selectUser } from 'src/app/core/store/selectors';
import { getFiltersFormModel } from 'src/app/core/utils/filter.utils';
import { isPresent } from 'src/app/core/utils/isPresent';
import { getWorkspacesFilterConditionsFromModel } from 'src/app/core/utils/workspaces.utils';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { WorkspaceDto, WorkspaceSearchModel } from '../../models/workspaces.model';
import { WorkspacesActions } from '../../store/actions';
import { selectWorkspaceCreating, selectWorkspaces } from '../../store/selectors';
import { AddWorkspaceModalComponent } from '../modals/add-workspace-modal/add-workspace-modal.component';
import { WorkspacesListEventsService } from '../workspaces-list/workspaces-list-events.service';

@UntilDestroy()
@Component({
  selector: 'app-workspaces',
  templateUrl: './workspaces.component.html',
  styleUrl: './workspaces.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class WorkspacesComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public criterions: Criterion[] = this.workspacesSearchCriterionsFactory.getAllItemsAsArray();
  public conditions: SearchItemModel[] | undefined;
  public form: FormGroup | undefined;
  public dataRecieved: boolean = false;
  public filteredWorkspaces: WorkspaceDto[] | null = [];
  public isWorkspaceCreating$ = this.store$.select(selectWorkspaceCreating);
  public canWrite$ = this.permissionService.canWrite$(PermissionResource.Workspaces);
  private canWriteWorkspaces = false;

  constructor(
    private readonly store$: Store,
    private titleService: Title,
    private bsModalService: BsModalService,
    private workspacesListEventsService: WorkspacesListEventsService,
    private workspacesSearchCriterionsFactory: WorkspacesSearchCriterionsFactory,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.canWrite$.pipe(untilDestroyed(this)).subscribe((canWrite) => {
      this.canWriteWorkspaces = canWrite;
      this.cdr.markForCheck();
    });
    this.waitUserReadyAndStart();
  }

  waitUserReadyAndStart(): void {
    this.store$
      .select(selectUser)
      .pipe(untilDestroyed(this), filter(isPresent), take(1))
      .subscribe((user) => {
        this.startInitialization();
      });
  }

  startInitialization(): void {
    this.store$.dispatch(WorkspacesActions.getWorkspaces());

    this.initForm();

    this.store$
      .select(selectWorkspaces)
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe((data) => {
        this.dataRecieved = !!data;
        this.cdr.markForCheck();
        setTimeout(() => {
          this.checkAndRestoreFilters();
        }, 1);
      });

    this.titleService.setTitle('AutoLex | Workspaces');
    this.workspacesListEventsService.setFilterCriterions(this.criterions);
  }

  initForm() {
    if (!this.form) {
      this.form = this.fb.group({
        workspaceName: [''],
      });

      this.initSubscriptions();
    }
    this.cdr.markForCheck();
  }

  checkAndRestoreFilters() {
    const filters = this.workspacesListEventsService.getFilters();

    if (filters?.length > 0) {
      const model = getFiltersFormModel(filters);
      this.form?.patchValue(model);
    } else {
      this.workspacesListEventsService.onFilter([]);
    }
  }

  initSubscriptions(): void {
    this.form?.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(50),
        tap((model: WorkspaceSearchModel) => {
          this.conditions = getWorkspacesFilterConditionsFromModel(
            model,
            this.workspacesSearchCriterionsFactory,
          );
        }),
      )
      .subscribe((value) => {
        this.workspacesListEventsService.resetPagination();

        if (!!this.conditions) {
          this.workspacesListEventsService.onFilter(this.conditions || []);
        }
      });

    this.workspacesListEventsService
      .getDataSubject()
      .pipe(untilDestroyed(this))
      .subscribe((data: any[]) => {
        this.filteredWorkspaces = data; // filtered
        this.cdr.markForCheck();
      });
  }

  addWorkspace() {
    if (!this.canWriteWorkspaces) {
      return;
    }

    this.bsModalService.show(AddWorkspaceModalComponent, {
      initialState: {},
      class: 'modal-xl',
    });
  }
}
